import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import StepProgress from '../../components/StepProgress/StepProgress';
import Button from '../../components/Button/Button';
import { CheckboxInline } from '../../components/FormField/CheckboxInline';
import {
  rescueEnableShelterluvMutation,
  rescueShelterluvStatusQuery,
  rescueResellerStatsQuery,
  RescueEnableShelterluvData,
} from '../../lib/integrationQueries';
import {
  trackShelterLuvStepCompleted,
  trackShelterLuvConnected,
  trackShelterLuvValidationFailed,
} from '../../analytics';
import styles from './Integrations.module.scss';

interface ShelterLuvWizardModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ShelterLuvWizardModal({ open, onClose, onSuccess }: ShelterLuvWizardModalProps) {
  const [step, setStep] = useState(1);
  const [apiKey, setApiKey] = useState('');
  const [apiKeyError, setApiKeyError] = useState<string | null>(null);
  const [backfillEnabled, setBackfillEnabled] = useState(true);
  const [incrementalEnabled, setIncrementalEnabled] = useState(true);
  const [validated, setValidated] = useState(false);
  const [validationResult, setValidationResult] = useState<{ orgName: string; chipsImported: number } | null>(null);

  const [enableShelterluv, { loading: validating }] =
    useMutation<RescueEnableShelterluvData>(rescueEnableShelterluvMutation);

  useEffect(() => {
    if (open) {
      setStep(1);
      setApiKey('');
      setApiKeyError(null);
      setValidated(false);
      setValidationResult(null);
      setBackfillEnabled(true);
      setIncrementalEnabled(true);
    }
  }, [open]);

  async function handleValidate() {
    if (!apiKey.trim()) {
      setApiKeyError('API key is required');
      return;
    }
    setApiKeyError(null);

    try {
      const { data } = await enableShelterluv({
        variables: {
          input: {
            apiKey: apiKey.trim(),
            thirdPartyChipBackfillEnabled: backfillEnabled,
            thirdPartyChipIncrementalEnabled: incrementalEnabled,
          },
        },
        refetchQueries: [{ query: rescueShelterluvStatusQuery }, { query: rescueResellerStatsQuery }],
      });

      if (data?.rescueEnableShelterluv) {
        const reseller = data.rescueEnableShelterluv;
        setValidated(true);
        setValidationResult({
          orgName: reseller.name,
          chipsImported: reseller.stats.chipsRegisteredOnShelterluv,
        });
        trackShelterLuvConnected(reseller.stats.chipsRegisteredOnShelterluv);
      }
    } catch (err: any) {
      trackShelterLuvValidationFailed();
      setApiKeyError(err.message || 'Invalid key \u2014 please check and try again');
    }
  }

  function handleDone() {
    onSuccess();
    onClose();
  }

  return (
    <Modal open={open} onClose={onClose} size="wide">
      <ModalTitle>{step === 1 ? 'Request your API key' : 'Paste your API key'}</ModalTitle>
      <StepProgress currentStep={step} totalSteps={2} />

      {step === 1 ? (
        <div className={styles.wizardStep}>
          <p className={styles.wizardText}>
            ShelterLuv&apos;s API lets us auto-register chips when you process adoptions &mdash; no manual data entry.
          </p>
          <p className={styles.wizardText}>
            To get started, you need an API key from ShelterLuv. Fill out their request form and they&apos;ll email you
            the key (may take a few days).
          </p>

          <a
            href="https://cdn.jotfor.ms/243268533362054"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.externalLink}
          >
            <Button variant="green" type="button">
              Request API Key from ShelterLuv
              <svg
                className={styles.externalLinkIcon}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
            </Button>
          </a>

          <p className={styles.wizardNote}>
            We recommend using a separate API key for each integration (e.g. one for Fi Nano, one for AKC Reunite).
          </p>

          <div className={styles.faqSection}>
            <details className={styles.faqItem}>
              <summary className={styles.faqQuestion}>How long does it take?</summary>
              <p className={styles.faqAnswer}>
                ShelterLuv support typically generates the key within 1&ndash;3 business days. You&apos;ll receive it
                via email.
              </p>
            </details>
            <details className={styles.faqItem}>
              <summary className={styles.faqQuestion}>What data syncs?</summary>
              <p className={styles.faqAnswer}>
                Adoption events, service outcomes, intake transfers, and owner surrenders. Pet name, breed, and
                microchip ID are synced automatically.
              </p>
            </details>
            <details className={styles.faqItem}>
              <summary className={styles.faqQuestion}>What if a pet is returned?</summary>
              <p className={styles.faqAnswer}>
                Intake events (transfers, owner surrenders) are logged but don&apos;t auto-clear chip owners. You can
                clear owners manually from the Registered tab.
              </p>
            </details>
          </div>

          <ModalActions>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="green"
              onClick={() => {
                trackShelterLuvStepCompleted(1, 'request_key');
                setStep(2);
              }}
            >
              I already have my key
            </Button>
          </ModalActions>
        </div>
      ) : (
        <div className={styles.wizardStep}>
          <p className={styles.wizardText}>
            Once ShelterLuv emails you the key, paste it below. We&apos;ll validate it and import your existing
            microchips.
          </p>

          <div className={styles.apiKeyField}>
            <label className={styles.fieldLabel} htmlFor="sl-api-key">
              ShelterLuv API Key
            </label>
            <div className={styles.apiKeyInputWrapper}>
              <input
                id="sl-api-key"
                type="text"
                className={`${styles.apiKeyInput} ${apiKeyError ? styles.apiKeyInputError : ''}`}
                value={apiKey}
                onChange={(e) => {
                  setApiKey(e.target.value);
                  if (apiKeyError) {
                    setApiKeyError(null);
                  }
                }}
                placeholder="Paste your API key here"
                disabled={validating || validated}
              />
              {validating && (
                <div className={styles.validationIcon}>
                  <svg className={styles.spinner} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" opacity="0.25" />
                    <path d="M12 2a10 10 0 0 1 10 10" />
                  </svg>
                </div>
              )}
              {validated && (
                <div className={styles.validationIcon}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    className={styles.checkIcon}
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
              )}
              {apiKeyError && !validating && (
                <div className={styles.validationIcon}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    className={styles.errorIcon}
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </div>
              )}
            </div>
            {apiKeyError && <span className={styles.fieldError}>{apiKeyError}</span>}
            {validated && validationResult && (
              <span className={styles.validationSuccess}>
                Connected to {validationResult.orgName} &mdash; {validationResult.chipsImported} chips imported
              </span>
            )}
          </div>

          <div className={styles.syncOptions}>
            <CheckboxInline
              id="sl-backfill"
              label="Import existing third-party microchips"
              checked={backfillEnabled}
              onChange={setBackfillEnabled}
            />
            <CheckboxInline
              id="sl-incremental"
              label="Sync new third-party microchips on adoption events"
              checked={incrementalEnabled}
              onChange={setIncrementalEnabled}
            />
          </div>

          <ModalActions>
            {validated ? (
              <Button variant="green" onClick={handleDone}>
                Done
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => setStep(1)}>
                  Back
                </Button>
                <Button variant="green" onClick={handleValidate} disabled={validating}>
                  {validating ? 'Validating...' : 'Validate & Connect'}
                </Button>
              </>
            )}
          </ModalActions>
        </div>
      )}
    </Modal>
  );
}
